import { z } from 'zod';
import { discordTime } from './discord-text.ts';
import { aUEC } from './money.ts';
import { Amount, EntryStatus, MAX_AMOUNT, RecordId } from './schemas.ts';

// Loans (plan 2A Assumption 6), after Sierra's loanDue, loanPaid, loanLeft and
// loanStatus (sierra-panel.html lines 2440–2448). What's paid comes from the
// ledger's completed repayments; the due date's end is an instant the server works
// out in the bank's time zone, so the browser and the server agree on Overdue.

export const LoanStatus = z.enum(['Active', 'Overdue', 'Paid', 'Written off']);
export type LoanStatus = z.infer<typeof LoanStatus>;

/** Sierra's LOAN_TONE: Active info, Overdue crit, Paid good, Written off mute. */
export const LOAN_TONE = { Active: 'info', Overdue: 'crit', Paid: 'good', 'Written off': 'mute' } as const satisfies Record<LoanStatus, string>;

/** What's owed in all: the principal plus the flat fee. */
export function loanDue(loan: { principal: number; fee: number }): number {
  return loan.principal + loan.fee;
}

/** What's left, never below zero: an overpayment leaves nothing, not a credit. */
export function loanLeft(due: number, paid: number): number {
  return Math.max(0, due - paid);
}

/**
 * Sierra's order: Written off, then Paid, then Overdue (strictly after the due
 * date's end), then Active.
 */
export function loanStatus(loan: { writtenOff: boolean; left: number; dueAt: string | null }, now: number): LoanStatus {
  if (loan.writtenOff) return 'Written off';
  if (loan.left <= 0) return 'Paid';
  if (loan.dueAt !== null && new Date(loan.dueAt).getTime() < now) return 'Overdue';
  return 'Active';
}

/** A calendar date, YYYY-MM-DD. */
export const CalendarDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'must be a date, YYYY-MM-DD');

/** A repayment as a loan card lists it. */
export const LoanPayment = z.object({ entryId: z.string(), amount: z.number().int(), at: z.iso.datetime(), status: EntryStatus });
export type LoanPayment = z.infer<typeof LoanPayment>;

export const Loan = z.object({
  id: z.string(),
  borrower: z.string(),
  purpose: z.string(),
  principal: z.number().int(),
  fee: z.number().int(),
  /** principal + fee. */
  due: z.number().int(),
  issuedOn: CalendarDate,
  dueOn: CalendarDate.nullable(),
  /** The last moment of the due date in the bank's time zone; after it, the loan is overdue. */
  dueAt: z.iso.datetime().nullable(),
  account: z.string(),
  notes: z.string(),
  writtenOffAt: z.iso.datetime().nullable(),
  /** From completed repayments, less any reversed. */
  paid: z.number().int(),
  left: z.number().int(),
  /** Paid beyond what was due (overpaying is allowed). */
  overpaid: z.number().int(),
  status: LoanStatus,
  /** The payout that lends it, if recorded: it counts as out only once that's completed. */
  lending: z.object({ entryId: z.string(), status: EntryStatus }).nullable(),
  /** Completed repayments, newest first, at most 3. */
  payments: z.array(LoanPayment),
  /** Repayments still waiting for an approver. */
  waiting: z.number().int(),
  /** Whether any of its entries completed; a loan without one can be deleted. */
  moved: z.boolean(),
  /** The borrower has lost access (plan 2A Assumption 9). */
  borrowerLeft: z.boolean(),
  createdBy: z.string(),
  version: z.number().int(),
  created: z.iso.datetime(),
});
export type Loan = z.infer<typeof Loan>;

/** Whether a loan is out: lent (its payout completed) and neither paid nor written off. */
export function isOut(loan: Pick<Loan, 'lending' | 'status'>): boolean {
  return loan.lending?.status === 'completed' && (loan.status === 'Active' || loan.status === 'Overdue');
}

const Purpose = z.string().trim().max(200).default('');
const Notes = z.string().trim().max(1000).default('');

export const LoanInput = z.object({
  borrower: RecordId,
  purpose: Purpose,
  principal: Amount,
  fee: z.number().int().min(0).max(MAX_AMOUNT).default(0),
  issuedOn: CalendarDate,
  dueOn: CalendarDate.nullable().default(null),
  /** The lending account. */
  account: RecordId,
  notes: Notes,
  /** Record the lending payout now (Sierra's "withdraw now", on by default). */
  lendNow: z.boolean().default(true),
});
export type LoanInput = z.input<typeof LoanInput>;

/** Editing changes only the purpose, the notes and the due date (plan 2A Assumption 6). */
export const LoanUpdate = z.object({ purpose: Purpose, notes: Notes, dueOn: CalendarDate.nullable(), version: z.number().int().min(1) });
export type LoanUpdate = z.input<typeof LoanUpdate>;

export const RepayInput = z.object({
  amount: Amount,
  /** Where the repayment goes; the lending account if left out. */
  account: RecordId.optional(),
  memo: z.string().trim().max(500).default(''),
});
export type RepayInput = z.input<typeof RepayInput>;

/** Sierra's loan reminder (line 2532), to paste into Discord. */
export function loanReminder(loan: Pick<Loan, 'purpose' | 'left' | 'dueAt'>, borrower: string, account: string | null): string {
  const due = loan.dueAt === null ? '' : `, due ${discordTime(new Date(loan.dueAt), 'D')} (${discordTime(new Date(loan.dueAt), 'R')})`;
  return `Hey ${borrower}, friendly reminder from ITC: your loan${loan.purpose === '' ? '' : ` for ${loan.purpose}`} has **${aUEC(loan.left)}** left to repay${due}. Send payments to ${account ?? 'the bank'} and let us know when it's sent. Thanks!`;
}
